import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
    EXTRACTABLE_TYPES, EventProductsService, ImportContext, ImportResult, ImportSource,
} from 'src/app/services/event-products/event-products.service';
import { parseCsv, decodeCsvBytes } from 'src/app/shared/product-import/csv-reader';
import { readXlsxRows } from 'src/app/shared/product-import/xlsx-reader';
import {
    ExistingProduct, IMPORT_FIELDS, ImportField, ImportFieldDef, ImportRow, ImportSummary, ImportZone, PreparedRow,
    guessMapping, isNewZoneKey, looksLikeHeader, newZoneNames, positionalMapping, prepareRows, rowsFromExtraction, rowsFromTable, summarize,
} from 'src/app/shared/product-import/product-import.model';
import { buildTemplateXlsx } from 'src/app/shared/product-import/product-export';

type Step = 'file' | 'columns' | 'review' | 'importing' | 'done';

/**
 * Importación masiva de productos a una zona de evento.
 *
 * Tres caminos según el archivo: Excel y CSV se leen en el navegador y pasan
 * por un paso de mapeo de columnas; PDF y capturas de pantalla van a la
 * función `extractProductsFromFile`, que los lee con IA y devuelve las filas
 * ya estructuradas. Los tres desembocan en la misma revisión: qué se crea,
 * qué se actualiza, qué se omite y por qué. Nada se escribe hasta confirmar.
 */
@Component({
    selector: 'app-import-products',
    templateUrl: './import-products.component.html',
    styleUrls: ['./import-products.component.scss'],
})
export class ImportProductsComponent implements OnInit {

    @Input() zone: ImportZone;
    @Input() eventKey: string = '';
    @Input() eventName: string = '';

    step: Step = 'file';
    fields: ImportFieldDef[] = IMPORT_FIELDS;
    accept = '.xlsx,.xlsm,.csv,.txt,.pdf,.png,.jpg,.jpeg,.webp';

    zones: ImportZone[] = [];
    zonesLoaded = false;

    fileName = '';
    source: ImportSource = 'xlsx';
    dragging = false;
    loading = false;
    loadingText = '';
    error = '';

    /** Archivo tabular: filas crudas, mapeo y si la primera fila es encabezado. */
    table: string[][] = [];
    mapping: ImportField[] = [];
    hasHeader = true;

    /** Notas que devolvió la IA al leer un PDF o una captura. */
    extractionNotes = '';

    rows: ImportRow[] = [];
    prepared: PreparedRow[] = [];
    summary: ImportSummary = { create: 0, update: 0, skip: 0, warnings: 0, newZones: 0 };
    onlyIssues = false;
    /**
     * Ubicaciones del archivo que no existen: crearlas como categorías de
     * esta zona (un archivo con "Botellas", "Tragos"… arma la barra completa)
     * o rechazar esas filas. Se decide en la revisión, antes de escribir.
     */
    createMissingZones = true;

    progress = 0;
    progressText = '';
    result: ImportResult = { created: 0, updated: 0, zones: 0 };

    private existingCache: { [zoneKey: string]: ExistingProduct[] } = {};

    constructor(
        private modalCtrl: ModalController,
        private products: EventProductsService,
        public auth: AuthService,
    ) { }

    async ngOnInit() {
        try {
            this.zones = await this.products.getEventZones(this.eventKey, this.zone);
        } catch (e) {
            console.error('No se pudieron leer las zonas del evento:', e);
            this.zones = [this.zone];
        }
        this.zonesLoaded = true;
    }

    close(data?: any) {
        this.modalCtrl.dismiss(data);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PASO 1: ARCHIVO
    // ═══════════════════════════════════════════════════════════════════════

    get zoneNames(): string {
        return this.zones.map(z => z.name).join(', ');
    }

    downloadTemplate() {
        const bytes = buildTemplateXlsx(this.zones.map(z => z.name), this.zone ? this.zone.name : '');
        this.download(
            new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            'Plantilla_productos_evento.xlsx',
        );
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        this.dragging = true;
    }

    onDragLeave() {
        this.dragging = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.dragging = false;
        const files = event.dataTransfer && event.dataTransfer.files;
        if (files && files.length > 0) this.loadFile(files[0]);
    }

    onFileInput(event: any) {
        const files: FileList = event.target && event.target.files;
        if (files && files.length > 0) this.loadFile(files[0]);
        // Permite volver a elegir el mismo archivo tras corregirlo.
        if (event.target) event.target.value = '';
    }

    private extensionOf(file: File): string {
        const name = String(file.name || '').toLowerCase();
        const dot = name.lastIndexOf('.');
        return dot >= 0 ? name.slice(dot + 1) : '';
    }

    async loadFile(file: File) {
        this.error = '';
        this.extractionNotes = '';
        this.fileName = file.name;
        const ext = this.extensionOf(file);
        const mime = EventProductsService.mimeOf(file);

        try {
            if (ext === 'xls') {
                throw new Error('Los .xls (Excel 97-2003) no se pueden leer. Ábrelo en Excel y guárdalo como .xlsx o CSV.');
            }
            if (ext === 'xlsx' || ext === 'xlsm') {
                this.source = 'xlsx';
                this.loading = true;
                this.loadingText = 'Leyendo el libro de Excel…';
                const sheet = await readXlsxRows(await this.readBuffer(file));
                this.setTable(sheet.rows);
            } else if (ext === 'csv' || ext === 'txt' || mime === 'text/csv' || mime === 'text/plain') {
                this.source = 'csv';
                this.loading = true;
                this.loadingText = 'Leyendo el CSV…';
                this.setTable(parseCsv(decodeCsvBytes(await this.readBuffer(file))));
            } else if (EXTRACTABLE_TYPES.indexOf(mime) >= 0) {
                this.source = mime === 'application/pdf' ? 'pdf' : 'image';
                this.loading = true;
                this.loadingText = mime === 'application/pdf'
                    ? 'Leyendo el PDF con IA… puede tardar un minuto.'
                    : 'Leyendo la captura con IA… puede tardar un minuto.';
                if (!this.zonesLoaded) await this.waitForZones();
                const extracted = await this.products.extractFromFile(file, this.zones);
                this.extractionNotes = extracted.notes;
                const rows = rowsFromExtraction(extracted.products);
                if (rows.length === 0) {
                    throw new Error('No se encontraron productos en el archivo. Revisa que la lista se vea completa y legible.');
                }
                await this.review(rows);
            } else {
                throw new Error('Formato no admitido. Sube un Excel (.xlsx), un CSV, un PDF o una imagen (PNG, JPG).');
            }
        } catch (e) {
            console.error('No se pudo leer el archivo:', e);
            this.error = this.messageOf(e);
            this.step = 'file';
        }
        this.loading = false;
    }

    private waitForZones(): Promise<void> {
        return new Promise(resolve => {
            const check = () => (this.zonesLoaded ? resolve() : setTimeout(check, 100));
            check();
        });
    }

    private readBuffer(file: File): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
        });
    }

    private messageOf(e: any): string {
        if (!e) return 'No se pudo leer el archivo.';
        // Errores de la función callable: code + message ya en español.
        if (e.message) return String(e.message);
        return String(e);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PASO 2: COLUMNAS (solo Excel y CSV)
    // ═══════════════════════════════════════════════════════════════════════

    private setTable(rows: string[][]) {
        if (!rows || rows.length === 0) {
            throw new Error('El archivo está vacío.');
        }
        const width = rows.reduce((max, r) => Math.max(max, r.length), 0);
        this.table = rows.map(r => {
            const row = r.slice();
            while (row.length < width) row.push('');
            return row;
        });
        this.hasHeader = looksLikeHeader(this.table[0]);
        this.mapping = this.hasHeader ? guessMapping(this.table[0]) : positionalMapping(width);
        this.step = 'columns';
    }

    get columns(): number[] {
        const width = this.table.length ? this.table[0].length : 0;
        const list: number[] = [];
        for (let i = 0; i < width; i++) list.push(i);
        return list;
    }

    get previewRows(): string[][] {
        return (this.hasHeader ? this.table.slice(1) : this.table).slice(0, 3);
    }

    get dataRowCount(): number {
        return Math.max(0, this.table.length - (this.hasHeader ? 1 : 0));
    }

    headerLabel(column: number): string {
        if (this.hasHeader && this.table[0][column]) return this.table[0][column];
        return `Columna ${column + 1}`;
    }

    onHeaderToggle() {
        const width = this.table.length ? this.table[0].length : 0;
        this.mapping = this.hasHeader ? guessMapping(this.table[0]) : positionalMapping(width);
    }

    /** Cada campo se puede asignar a una sola columna: al elegirlo se libera la otra. */
    onMappingChange(column: number, field: ImportField) {
        this.mapping = this.mapping.map((current, i) => {
            if (i === column) return field;
            return field !== 'ignore' && current === field ? 'ignore' : current;
        });
    }

    get hasNameColumn(): boolean {
        return this.mapping.indexOf('name') >= 0;
    }

    async continueToReview() {
        if (!this.hasNameColumn) return;
        this.error = '';
        this.loading = true;
        this.loadingText = 'Revisando las filas…';
        try {
            await this.review(rowsFromTable(this.table, this.mapping, this.hasHeader));
        } catch (e) {
            console.error('No se pudieron preparar las filas:', e);
            this.error = this.messageOf(e);
        }
        this.loading = false;
    }

    backToColumns() {
        this.step = this.source === 'xlsx' || this.source === 'csv' ? 'columns' : 'file';
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PASO 3: REVISIÓN
    // ═══════════════════════════════════════════════════════════════════════

    private async existingIn(zoneKey: string): Promise<ExistingProduct[]> {
        if (!this.existingCache[zoneKey]) {
            this.existingCache[zoneKey] = await this.products.getZoneProducts(zoneKey);
        }
        return this.existingCache[zoneKey];
    }

    private async review(rows: ImportRow[]) {
        this.rows = rows;
        if (rows.length === 0) {
            throw new Error('No hay filas con datos para importar.');
        }
        if (!this.zonesLoaded) await this.waitForZones();

        // Se consultan solo las zonas que el archivo menciona (más la actual);
        // una ubicación por crear todavía no tiene productos que consultar.
        const options = { zones: this.zones, defaultZone: this.zone, createMissingZones: this.createMissingZones };
        const first = prepareRows(rows, { ...options, existingByZone: {} });
        const zoneKeys = first.map(p => p.zoneKey).filter((k, i, all) => all.indexOf(k) === i && !isNewZoneKey(k));
        const existingByZone: { [zoneKey: string]: ExistingProduct[] } = {};
        for (const key of zoneKeys) existingByZone[key] = await this.existingIn(key);

        this.prepared = prepareRows(rows, { ...options, existingByZone });
        this.summary = summarize(this.prepared);
        this.onlyIssues = false;
        this.step = 'review';
    }

    /** Cambió la decisión sobre las ubicaciones nuevas: se vuelve a revisar el mismo archivo. */
    async onCreateMissingZonesChange(value: boolean) {
        this.createMissingZones = value;
        if (this.step !== 'review' || this.rows.length === 0) return;
        this.loading = true;
        this.loadingText = 'Revisando las filas…';
        try {
            await this.review(this.rows);
        } catch (e) {
            console.error('No se pudieron preparar las filas:', e);
            this.error = this.messageOf(e);
        }
        this.loading = false;
    }

    /** Categorías que se crearán dentro de la zona actual, para mostrarlas antes de importar. */
    get newZoneList(): string {
        return newZoneNames(this.prepared).join(', ');
    }

    get visibleRows(): PreparedRow[] {
        return this.onlyIssues ? this.prepared.filter(p => p.issues.some(i => i.level !== 'info')) : this.prepared;
    }

    get importable(): number {
        return this.summary.create + this.summary.update;
    }

    issueText(row: PreparedRow): string {
        return row.issues.map(i => i.message).join(' ');
    }

    hasError(row: PreparedRow): boolean {
        return row.issues.some(i => i.level === 'error');
    }

    onlyInfo(row: PreparedRow): boolean {
        return row.issues.length > 0 && row.issues.every(i => i.level === 'info');
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PASO 4: IMPORTAR
    // ═══════════════════════════════════════════════════════════════════════

    async runImport() {
        if (this.importable === 0 || this.step === 'importing') return;
        this.step = 'importing';
        this.progress = 0;
        this.progressText = 'Preparando…';
        this.error = '';

        const positions: { [zoneKey: string]: number } = {};
        Object.keys(this.existingCache).forEach(key => positions[key] = this.existingCache[key].length);
        const ctx: ImportContext = { eventKey: this.eventKey, source: this.source, fileName: this.fileName, zone: this.zone };

        try {
            this.result = await this.products.importProducts(this.prepared, ctx, positions, (done, total) => {
                this.progress = total > 0 ? done / total : 1;
                this.progressText = `${done} de ${total} productos`;
            });
            this.step = 'done';
        } catch (e) {
            console.error('Falló la importación:', e);
            this.error = 'No se pudieron guardar todos los productos. Revisa la conexión e intenta de nuevo: '
                + 'los que sí alcanzaron a crearse aparecen ahora como "actualizar".';
            // Se vuelve a revisar contra la base para reflejar lo que sí se escribió.
            this.existingCache = {};
            try {
                await this.review(this.rows);
            } catch (err) {
                this.step = 'review';
            }
        }
    }

    finish() {
        this.close({ imported: true, created: this.result.created, updated: this.result.updated, zones: this.result.zones || 0 });
    }

    private download(blob: Blob, filename: string) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    }
}
